/** The catalog keys the UI checks (webapp `modules/acl/shared/permissions/catalog.ts`). */
export const PERMISSION_KEYS = [
  'member.read',
  'member.invite',
  'member.update',
  'member.remove',
  'role.read',
  'machine_group.read',
  'machine_group.manage',
  'machine.external_name.update',
  'machine.production_settings.update',
  'machine.view_config.company_default.update',
  'shift.create',
  'shift.update',
  'shift.delete',
  'shift.exclusion.manage',
  'config.shift_settings.update',
  'goal.create',
  'stop.cause.manage',
  'stop.loss_tree.manage',
  'stop.component_group.manage',
  'stop.automation.manage',
  'stop.classification.create',
  'stop.classification.update',
  'operator.create',
  'operator.update',
  'operator.delete',
  'operator_plan.create',
  'operator_plan.update',
  'operator_plan.delete',
  'alert.group.create',
  'alert.group.update',
  'alert.group.delete',
  'alert.setting.create',
  'alert.setting.update',
  'alert.setting.delete',
  'alert.persona.create',
  'alert.persona.update',
  'alert.persona.delete',
  'alert.persona.legacy.manage',
  'alert.persona.push.enable',
  'notification.read_any',
  'sku.create',
  'sku.update',
  'sku.archive',
  'sku.delete',
  'sku.select_all',
  'nominal_speed.create',
  'nominal_speed.update',
  'nominal_speed.delete',
  'quality_issue.create',
  'quality_issue.update',
  'quality_issue.delete',
  'raw_material.create',
  'raw_material.update',
  'raw_material.delete',
  'work_order.update',
  'work_order.delete',
  'work_order.legacy_table.manage',
  'work_step.create',
  'work_step.clone',
  'work_step.update',
  'work_step.delete',
  'work_step.backdate',
  'production_advancement.nominal_speed.override',
  'company_configuration.update',
  'company_configuration.equipment_label.update',
  'api_key.read',
  'api_key.create',
  'api_key.delete'
] as const;

export type PermissionKey = typeof PERMISSION_KEYS[number];

/**
 * Any one of these opens the settings screen. Most screens use their write keys, so a member who
 * can only read the data does not get a page of actions that return 403. Production settings leaves
 * out `sku.delete` and `nominal_speed.create`: users hold them for the production forms, not for
 * this screen.
 */
export const SCREEN_PERMISSIONS = {
  usersSettings: ['member.read', 'role.read'],
  integrations: ['api_key.read'],
  machineSettings: ['machine_group.read'],
  shiftsNotConsidered: ['shift.exclusion.manage'],
  shiftSettings: ['shift.create', 'shift.update', 'shift.delete'],
  goals: ['goal.create'],
  stopSettings: [
    'stop.cause.manage',
    'stop.loss_tree.manage',
    'stop.component_group.manage',
    'stop.automation.manage'
  ],
  operatorSettings: [
    'operator.create',
    'operator.update',
    'operator.delete',
    'operator_plan.create',
    'operator_plan.update',
    'operator_plan.delete'
  ],
  alertSettings: [
    'alert.group.create',
    'alert.group.update',
    'alert.group.delete',
    'alert.setting.create',
    'alert.setting.update',
    'alert.setting.delete',
    'alert.persona.create',
    'alert.persona.update',
    'alert.persona.delete'
  ],
  productionSettings: [
    'sku.create',
    'sku.update',
    'sku.archive',
    'nominal_speed.update',
    'nominal_speed.delete',
    'quality_issue.create',
    'quality_issue.update',
    'quality_issue.delete',
    'raw_material.create',
    'raw_material.update',
    'raw_material.delete',
    'machine.external_name.update',
    'machine.production_settings.update'
  ]
} as const satisfies Record<string, readonly PermissionKey[]>;

export type PermissionScreen = keyof typeof SCREEN_PERMISSIONS;
